import React, { useState } from "react";

import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { LuAlertCircle, LuEye, LuEyeOff, LuLock } from "react-icons/lu";

import { POST } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";

interface IFormInput {
  password: string;
  newPassword: string;
  confirmPassword: string;
}

const PasswordChangeForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>({ mode: "onSubmit" });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);

    try {
      await POST("/v1/profile/password", {
        password: data.password,
        newPassword: data.newPassword,
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            reset({ password: "", newPassword: "", confirmPassword: "" });

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  const inputClass = (hasError: boolean) =>
    `flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
      hasError
        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
        : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
    }`;

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight">
          પાસવર્ડ બદલો
        </div>
        <div className="text-sm text-gray-600">તમારા પાસવર્ડને અપડેટ કરો.</div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="passwordChangeForm-password"
                className="text-sm font-semibold text-[#113F67]"
              >
                જૂનો પાસવર્ડ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuLock className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="passwordChangeForm-password"
                  type={showOldPassword ? "text" : "password"}
                  placeholder="તમારો જૂનો પાસવર્ડ દાખલ કરો"
                  {...register("password", {
                    required: "જૂનો પાસવર્ડ આવશ્યક છે",
                    minLength: {
                      value: 6,
                      message: "જૂનો પાસવર્ડ ઓછામાં ઓછો 6 અક્ષરનો હોવો જોઈએ",
                    },
                  })}
                  className={inputClass(!!errors.password)}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="absolute right-4 top-4 h-5 w-5 text-[#34699A] hover:text-[#113F67] transition-colors"
                >
                  {showOldPassword ? (
                    <LuEyeOff className="h-5 w-5" />
                  ) : (
                    <LuEye className="h-5 w-5" />
                  )}
                </button>
                {errors.password && (
                  <LuAlertCircle className="absolute right-12 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.password && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="passwordChangeForm-newPassword"
                className="text-sm font-semibold text-[#113F67]"
              >
                નવો પાસવર્ડ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuLock className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="passwordChangeForm-newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="તમારો નવો પાસવર્ડ દાખલ કરો"
                  {...register("newPassword", {
                    required: "નવો પાસવર્ડ આવશ્યક છે",
                    minLength: {
                      value: 6,
                      message: "નવો પાસવર્ડ ઓછામાં ઓછો 6 અક્ષરનો હોવો જોઈએ",
                    },
                    maxLength: {
                      value: 50,
                      message: "નવો પાસવર્ડ 50 અક્ષરથી વધુ લાંબો ન હોવો જોઈએ",
                    },
                    validate: {
                      upper: (value) =>
                        /[A-Z]/.test(value) ||
                        "નવા પાસવર્ડમાં ઓછામાં ઓછો એક મોટો અક્ષર હોવો જોઈએ",
                      lower: (value) =>
                        /[a-z]/.test(value) ||
                        "નવા પાસવર્ડમાં ઓછામાં ઓછો એક નાનો અક્ષર હોવો જોઈએ",
                      number: (value) =>
                        /[0-9]/.test(value) ||
                        "નવા પાસવર્ડમાં ઓછામાં ઓછો એક નંબર હોવો જોઈએ",
                      special: (value) =>
                        /[^A-Za-z0-9]/.test(value) ||
                        "નવા પાસવર્ડમાં ઓછામાં ઓછો એક ખાસ અક્ષર હોવો જોઈએ",
                    },
                  })}
                  className={inputClass(!!errors.newPassword)}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-4 top-4 h-5 w-5 text-[#34699A] hover:text-[#113F67] transition-colors"
                >
                  {showNewPassword ? (
                    <LuEyeOff className="h-5 w-5" />
                  ) : (
                    <LuEye className="h-5 w-5" />
                  )}
                </button>
                {errors.newPassword && (
                  <LuAlertCircle className="absolute right-12 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.newPassword && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="passwordChangeForm-confirmPassword"
                className="text-sm font-semibold text-[#113F67]"
              >
                કન્ફર્મ પાસવર્ડ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuLock className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="passwordChangeForm-confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="નવો પાસવર્ડ ફરીથી દાખલ કરો"
                  {...register("confirmPassword", {
                    required: "પાસવર્ડ કન્ફર્મ કરો",
                    validate: (value) =>
                      value === getValues("newPassword") ||
                      "નવો પાસવર્ડ અને કન્ફર્મ પાસવર્ડ મેળ ખાતા નથી",
                  })}
                  className={inputClass(!!errors.confirmPassword)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-4 h-5 w-5 text-[#34699A] hover:text-[#113F67] transition-colors"
                >
                  {showConfirmPassword ? (
                    <LuEyeOff className="h-5 w-5" />
                  ) : (
                    <LuEye className="h-5 w-5" />
                  )}
                </button>
                {errors.confirmPassword && (
                  <LuAlertCircle className="absolute right-12 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.confirmPassword && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  પાસવર્ડ બદલાઈ રહ્યો છે...
                </div>
              ) : (
                "પાસવર્ડ બદલો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default PasswordChangeForm;
